import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../stores/supabaseAuthStore';
import { ROUTES } from '../routes';

interface AuthCallbackPageProps {
  onSuccess?: () => void;
  onError?: () => void;
}

export const SupabaseAuthCallbackPage: React.FC<AuthCallbackPageProps> = ({
  onSuccess,
  onError,
}) => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing authentication...');
  const navigate = useNavigate();

  const { user, session, error, isLoading } = useAuth();

  useEffect(() => {
    const processCallback = async () => {
      try {
        // Check for OAuth errors in URL
        const urlParams = new URLSearchParams(window.location.search);
        const errorParam = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');

        if (errorParam) {
          setStatus('error');
          setMessage(errorDescription || `OAuth error: ${errorParam}`);
          setTimeout(() => {
            onError?.();
            navigate(ROUTES.LOGIN);
          }, 3000);
          return;
        }

        // Supabase automatically handles the OAuth callback and updates the auth state
        // We just need to wait for the session to be established
        setMessage('Completing authentication...');

        // Give Supabase a moment to process the callback
        await new Promise((resolve) => setTimeout(resolve, 1000));
      } catch (err) {
        console.error('Auth callback error:', err);
        setStatus('error');
        setMessage('Authentication failed. Please try again.');
        setTimeout(() => {
          onError?.();
          navigate(ROUTES.LOGIN);
        }, 3000);
      }
    };

    processCallback();
  }, [navigate, onError]);

  // Watch for auth state changes
  useEffect(() => {
    if (isLoading) {
      setStatus('loading');
      setMessage('Processing authentication...');
      return;
    }

    if (error) {
      setStatus('error');
      setMessage(error);
      setTimeout(() => {
        onError?.();
        navigate(ROUTES.LOGIN);
      }, 3000);
      return;
    }

    if (user && session) {
      setStatus('success');
      setMessage('Authentication successful! Redirecting...');
      setTimeout(() => {
        onSuccess?.();
        navigate(ROUTES.HOME);
      }, 2000);
      return;
    }

    // If no user after processing, show error
    if (status === 'loading') {
      setTimeout(() => {
        setStatus('error');
        setMessage('Authentication failed. No user session received.');
        setTimeout(() => {
          onError?.();
          navigate(ROUTES.LOGIN);
        }, 3000);
      }, 3000);
    }
  }, [user, session, error, isLoading, status, navigate, onSuccess, onError]);

  const getStatusIcon = () => {
    switch (status) {
      case 'loading':
        return <Loader2 className="h-8 w-8 animate-spin text-kind-purple" />;
      case 'success':
        return <CheckCircle className="h-8 w-8 text-green-400" />;
      case 'error':
        return <XCircle className="h-8 w-8 text-red-400" />;
      default:
        return <AlertCircle className="h-8 w-8 text-yellow-400" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'loading':
        return 'text-kind-purple';
      case 'success':
        return 'text-green-400';
      case 'error':
        return 'text-red-400';
      default:
        return 'text-yellow-400';
    }
  };

  const getStatusTitle = () => {
    switch (status) {
      case 'loading':
        return 'Authenticating...';
      case 'success':
        return 'Success!';
      case 'error':
        return 'Authentication Failed';
      default:
        return 'Processing...';
    }
  };

  return (
    <div className="min-h-screen bg-kind-deep flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-kind-graphite rounded-2xl shadow-xl p-8 border border-gray-300 dark:border-white/10 text-center">
          {/* Status Icon */}
          <div className="flex justify-center mb-6">{getStatusIcon()}</div>

          {/* Status Message */}
          <h2 className={`text-2xl font-bold mb-4 ${getStatusColor()}`}>{getStatusTitle()}</h2>

          <p className="text-kind-text-secondary text-center leading-relaxed">{message}</p>

          {/* Loading indicator for loading state */}
          {status === 'loading' && (
            <div className="mt-6">
              <div className="flex justify-center">
                <div className="animate-pulse flex space-x-2">
                  <div className="w-2 h-2 bg-kind-purple rounded-full"></div>
                  <div className="w-2 h-2 bg-kind-purple rounded-full"></div>
                  <div className="w-2 h-2 bg-kind-purple rounded-full"></div>
                </div>
              </div>
            </div>
          )}

          {/* Success indicator */}
          {status === 'success' && (
            <div className="mt-6">
              <div className="flex justify-center">
                <div className="flex items-center space-x-2 text-green-400">
                  <CheckCircle className="h-4 w-4" />
                  <span className="text-sm">Redirecting to dashboard...</span>
                </div>
              </div>
            </div>
          )}

          {/* Manual navigation for error state */}
          {status === 'error' && (
            <div className="mt-6 space-y-3">
              <button
                onClick={() => navigate(ROUTES.LOGIN)}
                className="w-full py-3 px-4 bg-kind-purple text-white rounded-lg hover:bg-kind-purple/90 transition-colors font-medium"
              >
                Return to Login
              </button>
              <button
                onClick={() => navigate(ROUTES.SIGNUP)}
                className="w-full py-3 px-4 bg-kind-container text-kind-text-primary rounded-lg hover:bg-kind-container/80 transition-colors font-medium border border-gray-300 dark:border-white/10"
              >
                Create New Account
              </button>
            </div>
          )}

          {/* Brand footer */}
          <div className="mt-8 pt-6 border-t border-gray-300 dark:border-white/10">
            <div className="flex items-center justify-center">
              <span className="text-kind-text-secondary text-sm font-title font-medium">
                OpenSelf
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
